/* eslint-disable no-shadow */
import React, { Component } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { bindActionCreators } from "redux"
import { Scoped } from "kremling"
import * as actions from "../../services/actions"
import { getLevelId } from "../components/utils"

// CSS
import styles from "../AdminDashboard.krem.css"

// Components
import CardHeader from "../components/CardHeader/CardHeader"
import Summary from "../Summary/Summary"
import AnalyzeRisks from "../AnalyzeRisks/AnalyzeRisks"
import CircleChart from "../components/CircleChart/CircleChart"
import OrgPath from "../components/OrgPath/OrgPath"
import Performers from "../components/Performers/Performers"
import { ScopedLoading } from "../components/Loading/Loading"

// Selectors
import {
  getBehaviorTypes,
  getHierarchicalScores,
  getOverallScore,
  getScoresDistributions,
  getTopDepartments,
  getRiskDistributions,
  getBehaviorMap,
  getTopPerformersId,
  getStragglersId,
  getCompanyLoaded
} from "../../services/selectors/visionSelectors"

class Company extends Component {
  componentDidMount() {
    const { actions, history, topPerformers } = this.props
    const { level, id } = getLevelId(history)
    actions.setPage(level, id)
    actions.resetColumn()
    actions.resetTab()
    // Check if data already exists
    if (Object.keys(topPerformers).length < 2) {
      actions.resetLoaded()
      actions.getBehaviorMap({
        level
      })

      actions.getTopBottomData({
        level,
        levelId: id,
        category: "score_overall",
        limit: 10,
        label: "stragglers"
      })

      actions.getTopBottomData({
        level,
        levelId: id,
        category: "score_overall",
        orderBy: " desc",
        limit: 10,
        label: "topPerformers"
      })

      actions.getBehaviorCategories({})

      actions.getTopDepartments({
        level,
        levelId: id,
        category: "score_overall",
        groupBy: "person_nid",
        limit: 5
      })

      actions.getHistoricalTrend({
        level,
        levelId: id
      })

      actions.getScoresDistributions({
        level,
        levelId: id
      })

      actions.getRiskDistributions({
        level,
        levelId: id
      })
    } else {
      actions.setLoaded()
      actions.sortColumn("name")
    }
  }

  componentDidUpdate() {
    const { loadedCount } = this.props
    if (loadedCount) {
      /**
       * Loaded Event
       *
       * This event allows other applications to know when
       * the vision dashboard has finished loading so they
       * can interact with it.
       */
      window.dispatchEvent(
        // eslint-disable-next-line no-undef
        new Event("vision:loaded", { bubbles: true, cancelable: false })
      )
    }
  }

  render() {
    const {
      history,
      monthlyScore,
      topPerformers,
      stragglers,
      behaviorTypes,
      topOverallScore,
      scoresDistributions,
      topDepartments,
      riskDistributions,
      behaviorMapData,
      loadedCount
    } = this.props

    const fullPath = [
      {
        id: "Vision",
        name: "Vision",
        level: "Vision",
        directHome: true
      },
      {
        id: "Company",
        name: "All",
        level: "Company",
        directHome: true
      }
    ]

    const shortPath = [
      {
        id: "Company",
        name: "All Departments",
        level: "Company",
        directHome: true
      }
    ]

    // Component Props
    const summaryProps = {
      topOverallScore,
      topDepartments,
      riskDistributions,
      level: "Company"
    }

    const behaviorMapTypes = ["name"].concat(behaviorTypes)

    const circleProps = {
      data: behaviorMapData,
      keys: behaviorMapTypes,
      title: "Behavior Map",
      childLevel: "department",
      history,
      headerClassName: "demo-tour-step-3"
    }

    const analyzeRisksProps = {
      scoresDistributions,
      monthlyScore,
      behaviorTypes
    }
    if (!loadedCount) return <ScopedLoading />

    return (
      <Scoped postcss={styles}>
        <div>
          {/* Company Name and Summary */}
          <div className="card">
            <OrgPath path={fullPath} size="small" />
            <OrgPath path={shortPath} size="large" />
            <div className="card-block mb-0 p-0">
              <Summary {...summaryProps} />
            </div>
          </div>

          {/* Departmental Behavior Map */}
          <CircleChart {...circleProps} />

          {/* Analyze Risks Tabs */}
          <div className="card">
            <CardHeader
              header={{ title: "Analyze Risks" }}
              noBottomBorder="border-bottom-0 mt-3"
              titleSize="h1"
              className="card"
            />
            <div className="card-block mb-0 p-0">
              <AnalyzeRisks {...analyzeRisksProps} />
            </div>
          </div>

          {/* High and Low Performers */}
          <Performers topPerformers={topPerformers} stragglers={stragglers} />
        </div>
      </Scoped>
    )
  }
}

Company.propTypes = {
  actions: PropTypes.shape({
    setPage: PropTypes.func,
    resetColumn: PropTypes.func,
    resetTab: PropTypes.func,
    getTopBottomData: PropTypes.func,
    getBehaviorCategories: PropTypes.func,
    getHistoricalTrend: PropTypes.func,
    getScoresDistributions: PropTypes.func,
    getRiskDistributions: PropTypes.func,
    getTopDepartments: PropTypes.func,
    getBehaviorMap: PropTypes.func,
    resetLoaded: PropTypes.func,
    setLoaded: PropTypes.func,
    sortColumn: PropTypes.func
  }).isRequired,
  history: PropTypes.shape({}).isRequired,
  topPerformers: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  behaviorTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  stragglers: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  scoresDistributions: PropTypes.shape({}).isRequired,
  monthlyScore: PropTypes.arrayOf(PropTypes.shape()),
  topOverallScore: PropTypes.number,
  riskDistributions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  topDepartments: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  behaviorMapData: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  loadedCount: PropTypes.bool.isRequired
}

Company.defaultProps = {
  topOverallScore: null,
  monthlyScore: null
}

function mapStateToProps(state, ownProps) {
  return {
    behaviorTypes: getBehaviorTypes(state),
    monthlyScore: getHierarchicalScores(state),
    topPerformers: getTopPerformersId(state, ownProps),
    stragglers: getStragglersId(state, ownProps),
    topOverallScore: getOverallScore(state),
    scoresDistributions: getScoresDistributions(state),
    topDepartments: getTopDepartments(state),
    riskDistributions: getRiskDistributions(state),
    behaviorMapData: getBehaviorMap(state),
    loadedCount: getCompanyLoaded(state)
  }
}

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    actions: bindActionCreators(actions, dispatch)
  }
}

// export Company
export default connect(mapStateToProps, mapDispatchToProps)(Company)
